<script setup lang="ts">
import { computed } from "vue";

type Size = "xl" | "l" | "m" | "s";
type Tag = "h1" | "h2" | "h3" | "h4" | "h5" | "h6";

const props = defineProps<{
  size?: Size;
  tag: Tag;
}>();

const COMPONENT_CLASS = "le-heading";

const defaultSize = (tag: Tag): Size => {
  switch (tag) {
    case "h1":
      return "xl";
    case "h2":
      return "l";
    case "h3":
      return "m";
  }

  // H4 and below get the small size
  return "s";
};

const size = computed<Size>(() => props.size ?? defaultSize(props.tag));

const componentClasses = computed<string[]>(() => [COMPONENT_CLASS, `${COMPONENT_CLASS}--size-${size.value}`]);
</script>

<template>
  <Component :is="props.tag" :class="componentClasses">
    <slot />
  </Component>
</template>

<style scoped lang="less">
@import "@dsk/styles/all.less";

.le-heading {
  margin: 0;
  color: @colors[@neutrals][500];
  font-family: @font-family-heading;
  font-weight: 500;

  &--size-xl,
  &--size-l {
    font-size: 1.625rem;
    line-height: 2.25rem;

    #media-breakpoint-up(md, {
      font-size: 2.5rem;
      line-height: 3.375rem;
    });
  }

  &--size-l {
    #media-breakpoint-up(md, {
      font-size: 1.875rem;
      line-height: 2.625rem;
    });
  }

  &--size-m {
    font-size: 1.375rem;
    line-height: 1.875rem;

    #media-breakpoint-up(md, {
      font-size: 1.5rem;
      line-height: 2.125rem;
    });
  }

  &--size-s {
    font-size: 1.125rem;
    line-height: 1.625rem;

    #media-breakpoint-up(md, {
      font-size: 1.25rem;
      line-height: 1.75rem;
    });
  }
}
</style>
